import React, {useState} from 'react';
import {observer} from 'mobx-react';
import {Button, FormInput, Icon, Modal} from '@vidazoo/ui-kit';
import clsx from 'clsx';
import useReportingModel from '../../common/hooks/useReportingModel';
import {compileDynamicFormula} from '../../common/utils/dynamicFormula';
import {IRecentDynamicFormula} from '../../../../common/utils/RecentDynamicFormulas';
import FormulaInput from './FormulaInput';
import theme from './theme.module.scss';

/** The user's last created formulas; picking one fills the editor without saving. */
const RecentFormulas = observer(function RecentFormulas({onPick}: {onPick: (formula: IRecentDynamicFormula) => void}) {
    const model = useReportingModel();
    const recent = model.localStorageManager.recentDynamicFormulas;
    if (!recent.items.length) return null;

    const fitsReport = (expression: string) => {
        try {
            compileDynamicFormula(expression, model.formulaMetrics);
            return true;
        } catch {
            return false;
        }
    };

    return (
        <section className={theme.recent} aria-label="Recent formulas">
            <div className={theme.recentTitle}>Recent formulas</div>
            <ul className={theme.recentList}>
                {recent.items.map(formula => {
                    const fits = fitsReport(formula.expression);
                    return (
                        <li key={formula.expression} className={clsx(theme.recentItem, !fits && theme.unavailable)}>
                            <button type="button" className={theme.recentPick} onClick={() => onPick(formula)}
                                    title={fits ? 'Use this formula' : 'Some metrics in this formula are not in the report'}>
                                <span className={theme.recentName}>{formula.name}</span>
                                <span className={theme.recentExpression}>{formula.expression}</span>
                                {!fits && <span className={theme.recentNote}>Uses metrics that are not in this report</span>}
                            </button>
                            <button type="button" className={theme.recentRemove} aria-label={`Remove ${formula.name} from recent formulas`}
                                    title="Remove" onClick={() => recent.remove(formula.expression)}>
                                <Icon iconName="icon-cancel-small" size={1.4}/>
                            </button>
                        </li>
                    );
                })}
            </ul>
        </section>
    );
});

const FormulaEditor = observer(function FormulaEditor() {
    const model = useReportingModel();
    const draft = model.dynamicFormulaDraft;
    const [name, setName] = useState(draft.name);
    const [expression, setExpression] = useState(draft.expression);
    const [error, setError] = useState('');
    return <form className={theme.editor} aria-label="Dynamic formula" onSubmit={event => {
        event.preventDefault();
        try { model.saveDynamicFormula(name, expression); }
        catch (error) { setError(error.message); }
    }}>
        <FormulaInput value={expression} metrics={model.formulaMetrics} onChange={setExpression}/>
        <FormInput label="Name" name="name" aria-label="Formula name" value={name}
            placeholder="Name" hideValidationMessage hideWhiteSpacesMessage onChange={event => setName(event.target.value)}/>
        {error && <div role="alert" className={theme.error}>{error}</div>}
        <RecentFormulas onPick={formula => {
            setExpression(formula.expression);
            setName(formula.name);
            setError('');
        }}/>
        <div className={theme.actions}>
            <Button type="button" uppercase gray medium onClick={model.closeDynamicFormula}>Cancel</Button>
            <Button type="submit" uppercase green medium>{draft.key ? 'Update' : 'Create'}</Button>
        </div>
    </form>;
});

export default observer(function DynamicFormulaModal() {
    const model = useReportingModel();
    return <Modal exitOnClickOutside isOpen={!!model.dynamicFormulaDraft} onClose={model.closeDynamicFormula}>
        {model.dynamicFormulaDraft && <FormulaEditor/>}
    </Modal>;
});
